import { useState } from 'react';

interface Step {
  number: number;
  title: string;
  description: string;
  icon: string;
  tips?: string;
}

interface StepByStepGuideProps {
  title: string;
  steps: Step[];
  color?: 'cyan' | 'teal' | 'blue' | 'purple' | 'amber';
}

export default function StepByStepGuide({ title, steps, color = 'cyan' }: StepByStepGuideProps) {
  const [activeStep, setActiveStep] = useState<number | null>(null);

  const colorClasses = {
    cyan: {
      gradient: 'from-cyan-500 to-teal-500',
      bg: 'bg-cyan-50',
      border: 'border-cyan-200',
      text: 'text-cyan-600',
      activeBg: 'bg-cyan-500',
      hoverBg: 'hover:bg-cyan-50',
      line: 'bg-cyan-300',
    },
    teal: {
      gradient: 'from-teal-500 to-emerald-500',
      bg: 'bg-teal-50',
      border: 'border-teal-200',
      text: 'text-teal-600',
      activeBg: 'bg-teal-500',
      hoverBg: 'hover:bg-teal-50',
      line: 'bg-teal-300',
    },
    blue: {
      gradient: 'from-blue-500 to-indigo-500',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-600',
      activeBg: 'bg-blue-500',
      hoverBg: 'hover:bg-blue-50',
      line: 'bg-blue-300',
    },
    purple: {
      gradient: 'from-purple-500 to-pink-500',
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-600',
      activeBg: 'bg-purple-500',
      hoverBg: 'hover:bg-purple-50',
      line: 'bg-purple-300',
    },
    amber: {
      gradient: 'from-amber-500 to-orange-500',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-600',
      activeBg: 'bg-amber-500',
      hoverBg: 'hover:bg-amber-50',
      line: 'bg-amber-300',
    },
  };

  const colors = colorClasses[color];

  return (
    <div className={`my-8 ${colors.bg} rounded-xl border ${colors.border} overflow-hidden`}>
      <div className={`px-6 py-4 bg-gradient-to-r ${colors.gradient} text-white`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
            <i className="ri-route-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-bold text-lg">{title}</h3>
            <p className="text-white/90 text-sm">{steps.length}ステップで完了</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* デスクトップ版：横並びフロー */}
        <div className="hidden md:block">
          <div className="flex items-start justify-between relative">
            {/* 接続線 */}
            <div className="absolute top-12 left-0 right-0 h-1 flex items-center px-16">
              <div className={`flex-1 h-0.5 ${colors.line}`}></div>
            </div>

            {steps.map((step, index) => (
              <div key={step.number} className="flex-1 relative z-10">
                <div className="flex flex-col items-center">
                  {/* ステップ番号 */}
                  <button
                    onClick={() => setActiveStep(activeStep === step.number ? null : step.number)}
                    className={`w-24 h-24 flex items-center justify-center rounded-full transition-all cursor-pointer ${
                      activeStep === step.number
                        ? `${colors.activeBg} text-white shadow-lg scale-110`
                        : 'bg-white text-gray-400 border-2 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <i className={`${step.icon} text-2xl block mb-1`}></i>
                      <span className="text-xs font-bold">STEP {step.number}</span>
                    </div>
                  </button>

                  {/* ステップタイトル */}
                  <div className="mt-4 text-center px-2">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">{step.title}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{step.description}</p>
                  </div>

                  {/* 詳細情報（展開時） */}
                  {activeStep === step.number && step.tips && (
                    <div className={`mt-4 p-3 ${colors.bg} border ${colors.border} rounded-lg text-left w-full max-w-xs`}>
                      <div className="flex items-start gap-2">
                        <div className={`w-5 h-5 flex items-center justify-center ${colors.text} flex-shrink-0 mt-0.5`}>
                          <i className="ri-lightbulb-line text-sm"></i>
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed">{step.tips}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* モバイル版：縦並びフロー */}
        <div className="md:hidden space-y-4">
          {steps.map((step, index) => (
            <div key={step.number}>
              <button
                onClick={() => setActiveStep(activeStep === step.number ? null : step.number)}
                className={`w-full p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  activeStep === step.number
                    ? `${colors.border} ${colors.bg}`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* ステップ番号 */}
                  <div className={`w-16 h-16 flex items-center justify-center rounded-full flex-shrink-0 ${
                    activeStep === step.number
                      ? `${colors.activeBg} text-white`
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    <div className="text-center">
                      <i className={`${step.icon} text-xl block mb-0.5`}></i>
                      <span className="text-xs font-bold">STEP {step.number}</span>
                    </div>
                  </div>

                  {/* ステップ内容 */}
                  <div className="flex-1 text-left">
                    <h4 className="font-bold text-gray-900 mb-1">{step.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>

                  {/* 展開アイコン */}
                  <div className={`w-6 h-6 flex items-center justify-center text-gray-400 transition-transform ${
                    activeStep === step.number ? 'rotate-180' : ''
                  }`}>
                    <i className="ri-arrow-down-s-line text-xl"></i>
                  </div>
                </div>

                {/* 詳細情報（展開時） */}
                {activeStep === step.number && step.tips && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-start gap-2">
                      <div className={`w-5 h-5 flex items-center justify-center ${colors.text} flex-shrink-0 mt-0.5`}>
                        <i className="ri-lightbulb-line"></i>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{step.tips}</p>
                    </div>
                  </div>
                )}
              </button>

              {/* 接続線（最後以外） */}
              {index < steps.length - 1 && (
                <div className="flex justify-center py-2">
                  <div className={`w-0.5 h-6 ${colors.line}`}></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 完了メッセージ */}
        <div className={`mt-6 p-4 bg-white border ${colors.border} rounded-lg`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center ${colors.activeBg} text-white rounded-full`}>
              <i className="ri-check-double-line text-xl"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-900">すべてのステップを完了すると</p>
              <p className="text-sm text-gray-600">問題が解決し、安定した配信が可能になります</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
